<template>
  <!-- container element for the page -->
  <div class="page-influencer-insight">
    <!-- If some user has been selected to be shown details of -->
    <template v-if="shouldShowResults && computedUsername">
      <!-- Show toggle buttons and search input -->
      <div class="d-flex justify-center justify-md-end flex-wrap options-container">
        <!-- show the switches for different pages -->
        <!-- <page-switch :items="navigationItems" :default-index="0" /> -->

        <div class="d-flex mt-3 mt-sm-0">
          <!-- show the input for searching terms -->
          <div class="search-input search-input--contain">
            <profile-selector
              v-model="form.search"
              @change="handleProfileChange"
              :platform="selectedPlatform"
              :use-combobox="true"
              :hide-no-data="true"
              type="search"
              label="Search Profile"
              dense
              flat
              solo
            />
          </div>
          <!-- the select options for different platforms -->
          <platform-selector
            :value="selectedPlatform"
            class="platform-selector"
            @input="handlePlatformChange"
          />
        </div>
      </div>

      <div class="mt-4 mt-md-6 results-container">
        <user-profile
          :platform="selectedPlatform"
          :username="computedUsername"
          :account-id="computedAccountId"
          :key="`${selectedPlatform}-${computedUsername}`"
        />
      </div>
    </template>

    <!-- Otherwise, show the input at the center of the page for better UI -->
    <v-card
      v-else
      max-width="700"
      class="mx-auto accent mt-sm-8 mt-md-12 mt-lg-16"
      flat
    >
      <div class="d-flex flex-wrap align-center justify-space-between pr-md-4">
        <div>
          <v-card-title>
            Influencer Insight
          </v-card-title>

          <v-card-subtitle class="grey--text text--darken-2">
            Search and select an influencer to see their insights report
          </v-card-subtitle>
        </div>

        <v-btn
          v-if="isBulkDownloadPossible"
          :to="{ name: 'BulkInsightsExport' }"
          class="shadow--primary ml-4 ml-md-0"
          color="primary"
          depressed
          large
        >
          Export In Bulk
        </v-btn>
      </div>

      <v-card-text>
        <div class="d-flex mt-3 mt-sm-0">
          <!-- show the input for searching terms -->
          <div class="search-input">
            <profile-selector
              v-model="form.search"
              @change="handleProfileChange"
              :platform="selectedPlatform"
              :use-combobox="true"
              :hide-no-data="true"
              type="search"
              label="Search Profile"
              dense
              flat
              solo
            />
          </div>
          <!-- the select options for different platforms -->
          <platform-selector
            :value="selectedPlatform"
            class="platform-selector"
            @input="handlePlatformChange"
          />
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import platformRegex from "@/helpers/platformRegex"

// const PageSwitch = () => import("@/blocks/common/PageSwitch.vue")
const UserProfile = () => import("@/components/common/UserProfile.vue")
const ProfileSelector = () => import("@/blocks/common/selectors/ProfileSelector.vue")
const PlatformSelector = () => import("@/blocks/common/selectors/PlatformSelector.vue")

const moduleName = "influencerInsight"

export default {
  name: "InfluencerInsight",

  components: {
    // PageSwitch,
    UserProfile,
    ProfileSelector,
    PlatformSelector,
  },

  data: () => ({
    shouldShowResults: false,

    // navigationItems: [
    //   {
    //     text: "Influencer Insight",
    //     link: {
    //       name: "InfluencerInsight"
    //     }
    //   },
    //   {
    //     text: "Influencer Discovery",
    //     link: {
    //       name: "InfluencerDiscovery"
    //     }
    //   }
    // ],

    form: {
      search: null
    },

    selectedUser: null
  }),

  computed: {
    /**
     * The current selected platform
     *
     * @return {String}
     */
    selectedPlatform() {
      return this.$store.getters[`${moduleName}/selectedPlatform`]
    },

    /**
     * The sanitized username that's entered by the user
     *
     * @return {String|Null}
     */
    computedUsername() {
      return this.form.search ? this.form.search.idToQuery || this.form.search : null
    },

    /**
     * If the user selected the profile from the dropdown, use the account Id
     *
     * @return {String|Null}
     */
    computedAccountId() {
      return this.selectedUser ? this.selectedUser.user_id : null
    },

    /**
     * Whether or not to show the bulk downloads link
     *
     * @return {Boolean}
     */
    isBulkDownloadPossible() {
      return this.$store.getters["auth/isServiceAllowed"](constants.model.user.allowedServices.actionBulkExportInfluencerInsightsPDF) || this.$store.getters["auth/isServiceAllowed"](constants.model.user.allowedServices.actionBulkExportInfluencerInsightsXLSX)
    }
  },

  methods: {
    /**
     * Every time the user selects or submits the user input form, call this function
     *
     * @param {Object} selectedUser
     * @return {void}
     */
    handleProfileChange(selectedUser) {
      if (!this.form.search) return false

      // check if the value is a URL, if yes then try to extract the value from it
      if (isURL(selectedUser)) {
        const match = selectedUser.match(platformRegex[this.selectedPlatform])

        if (match !== null && match[1]) {
          this.selectedUser = match[1]
          this.shouldShowResults = true

          // also update the value of the form input
          this.form.search = match[1]

          // return to make sure the function ends here
          return true
        }
      }

      // final fallback option
      this.selectedUser = selectedUser
      this.shouldShowResults = true
    },

    /**
     * Whenever the user switches the platform selected, reset the form
     *
     * @param {String} value
     * @returns {void}
     */
    handlePlatformChange(value) {
      // hide the current report
      this.form.search = null
      this.selectedUser = null
      this.shouldShowResults = false

      this.$store.dispatch(`${moduleName}/setSelectedPlatform`, value)
    }
  },
}
</script>

<style lang="stylus">
// to contain the entire box structure in large viewport
.page-influencer-insight
  .options-container
    max-width 1120px
    margin 0 auto

  .results-container
    max-width 1320px
    margin 0 auto

  .search-input
    width 100%

    &--contain
      @media (min-width 960px)
        width 18em

    .v-input__slot
      height 48px
      border-top-right-radius 0
      border-bottom-right-radius 0

    .v-label
      padding-top 4px

  .platform-selector
    .v-input__slot
      border-top-left-radius 0
      border-bottom-left-radius 0
</style>
